import { View, Button } from "@/components/ui";
import { NotificationItem } from "@/components/shared/notifications/notification-item";
import { EmptyCard } from "@/components/shared/empty-card";
import NoNotification from "@/assets/images/3d-illus/no-notification.png";
import { ReadStatusFilter, type ReadStatus } from "../dropdown-status-filter";
import { useEffect, useState } from "react";
import { LoadingDialog } from "@/components/shared/loading-dialog";
import { useRoute } from "@/components/shared/router";
import type { Notification } from "@/types/notification";
import { useMarkAllAsReadNotificationMutation } from "@/gqlhooks/notification/useMarkAllAsReadNotificationMutation";
import { useReadNotificationMutation } from "@/gqlhooks/notification/useReadNotificationMutation";
import { useClientNotificationsQuery } from "@/gqlhooks/notification/useClientNotificationsQuery";
import { getFragmentData } from "@/__generated__";
import { ClientNotificationDetailFragmentDoc } from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";

const PER_PAGE = 10;

export function NotificationList() {
  const { push } = useRoute();
  const [readStatus, setReadStatus] = useState<ReadStatus>("All");

  const isRead = readStatus === "All" ? undefined : readStatus === "Read";

  const [markAllAsRead, { loading: markAllAsReadLoading }] =
    useMarkAllAsReadNotificationMutation();
  const [readNotificationFn] = useReadNotificationMutation();
  const {
    list,
    canLoadMore,
    loading: notificationsLoading,
    refetch,
    fetchMore,
  } = useClientNotificationsQuery({ isRead, offset: 0, limit: PER_PAGE });

  const navigateToNotificationDetail = (notification: Notification) => {
    if (!notification.isRead) {
      readNotificationFn({
        variables: {
          input: {
            id: notification.id,
          },
        },
      });
    }
    push(`/notifications/${notification.id}`);
  };

  const onLoadMore = () => {
    fetchMore({
      variables: {
        filters: {
          isRead,
          offset: list.length,
          limit: PER_PAGE,
        },
      },
    });
  };

  const unreadNotificationCount = useAuthState(
    (state) => state.data?.unreadNotificationCount,
  );

  useEffect(() => {
    if (unreadNotificationCount) {
      refetch({
        filters: {
          isRead,
          offset: 0,
          limit: PER_PAGE,
        },
      });
    }
  }, [unreadNotificationCount]);

  return (
    <View className="border-t border-stone-100 sm:border-0">
      <View className="m-4 flex flex-row justify-between">
        <ReadStatusFilter
          status={readStatus}
          onStatusChange={(status) => setReadStatus(status)}
        />
        <Button
          variant="tertiary"
          color="CTA2"
          size="md"
          children="Mark all as read"
          onClick={() => markAllAsRead()}
        />
      </View>
      {list.length ? (
        <View className="mb-4">
          {list.map((item, index) => {
            const notification = getFragmentData(
              ClientNotificationDetailFragmentDoc,
              item,
            );
            return (
              <NotificationItem
                key={index}
                item={notification}
                handlePress={() => navigateToNotificationDetail(notification)}
              />
            );
          })}
          {canLoadMore && (
            <Button
              variant="tertiary"
              color="CTA2"
              fullWidth="full"
              loading={notificationsLoading}
              onClick={onLoadMore}
              children="Load more"
              className="mt-4"
            />
          )}
        </View>
      ) : (
        <EmptyCard
          src={NoNotification}
          title="You don't have any notification"
          desc="We'll notify you when there's something worth your attention"
        />
      )}
      <LoadingDialog
        open={markAllAsReadLoading}
        label="Loading notifications..."
      />
    </View>
  );
}
